// src/components/navigation/MobileTopBar.jsx

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, LogOut, User, HelpCircle, Info, Users, UserCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../utils/cn';

export function MobileTopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isProvider, isAdmin } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  // Close menu on navigation
  useEffect(() => {
    setShowMenu(false);
  }, [location]);

  const showBackButton = location.pathname !== '/program';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div 
      className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-40 lg:hidden"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="flex items-center justify-between h-14 px-4">
        {/* Left: Back button or spacer */}
        <div className="flex-1 flex items-center">
          {showBackButton ? (
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 rounded-full hover:bg-gray-100"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
          ) : (
            <div className="w-9" />
          )}
        </div>

        {/* Center: Brand */}
        <div className="flex items-center">
          <h1 className="text-lg font-bold text-primary">ACL Care</h1>
        </div>

        {/* Right: User menu */}
        <div className="flex-1 flex items-center justify-end">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center"
            aria-label="User menu"
          >
            <span className="text-primary font-semibold text-sm">
              {user?.first_name?.[0]?.toUpperCase() || 'U'}
            </span>
          </button>
        </div>
      </div>

      {/* Dropdown Menu */}
      {showMenu && (
        <>
          <div
            className="fixed inset-0 bg-black/20 z-40"
            onClick={() => setShowMenu(false)}
          />

          <div
            ref={menuRef}
            className="absolute top-full right-4 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
          >
            {/* User info */}
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {user?.email}
              </p>
            </div>

            {/* Menu items */}
            <div className="py-1">
              {/* Admin/Provider section */}
              {(isProvider || isAdmin) && (
                <>
                  <button
                    onClick={() => navigate('/clients')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                  >
                    <UserCircle className="h-4 w-4 mr-2" />
                    Clients
                  </button>

                  {isAdmin && (
                    <button
                      onClick={() => navigate('/users')}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Admin
                    </button>
                  )}

                  <div className="border-t border-gray-100 my-1" />
                </>
              )}

              <button
                onClick={() => navigate('/about')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
              >
                <Info className="h-4 w-4 mr-2" />
                About
              </button>

              <button
                onClick={() => navigate('/faq')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
              >
                <HelpCircle className="h-4 w-4 mr-2" />
                FAQs
              </button>

              <div className="border-t border-gray-100 my-1" />

              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}